import { DEFAULT_QUIZ } from '../data/defaultQuiz';

// --- Text Replacement Helper ---
export function doGenderNameReplacements(text, userName, otherName, otherGender) {
  if (!text) return '';
  let processed = text;

  // Name replacements
  const name = otherName || 'the client';
  // Replace [Name] and [name] globally
  processed = processed.replace(/\[Name\]/g, name);
  processed = processed.replace(/\[name\]/g, name);

  // Gender replacements
  // Default to male if not specified, or handle 'unknown' if required. 
  // Legacy logic typically defaulted or used 'he'/'she'.
  const isMale = !otherGender || otherGender.toLowerCase() === 'male';

  // Subject pronouns
  processed = processed.replace(/\[He\]/g, isMale ? 'He' : 'She');
  processed = processed.replace(/\[he\]/g, isMale ? 'he' : 'she');

  // Possessive pronouns
  processed = processed.replace(/\[His\]/g, isMale ? 'His' : 'Her');
  processed = processed.replace(/\[his\]/g, isMale ? 'his' : 'her');

  // Object pronouns
  processed = processed.replace(/\[Him\]/g, isMale ? 'Him' : 'Her');
  processed = processed.replace(/\[him\]/g, isMale ? 'him' : 'her');

  // Reflexive
  processed = processed.replace(/\[Himself\]/g, isMale ? 'Himself' : 'Herself');
  processed = processed.replace(/\[himself\]/g, isMale ? 'himself' : 'herself');

  return processed;
}

// --- Helper: Get Score Detail Texts (from score-detail.xml logic) ---
function getScoreDetailTexts(bestType, pScore, meetingIndex, personName, gender, scoreDetailData) {
  const texts = [];
  
  // 1. Find the type data
  const typeData = scoreDetailData[bestType];
  if (!typeData || !Array.isArray(typeData)) return texts;

  // 2. Find the detail band matching the pScore
  // Ranges can be "50-59", "60-69", "70-79", etc. or exact "50"
  const detailBand = typeData.find(band => {
    const range = band.scoreRange;
    if (!range) return false;
    
    if (range.includes('-')) {
      const [min, max] = range.split('-').map(Number);
      return pScore >= min && pScore <= max;
    } else {
      return pScore === Number(range);
    }
  });

  if (!detailBand || !detailBand.meetings) return texts;

  // 3. Select the meeting (1, 2, or 3 for 3+)
  // Legacy logic: if meetingIndex > 3, use 3.
  let mId = String(meetingIndex);
  if (parseInt(mId) > 3) mId = '3';
  
  const rawItems = detailBand.meetings[mId];
  
  if (Array.isArray(rawItems)) {
    rawItems.forEach(item => {
      texts.push(doGenderNameReplacements(item, null, personName, gender));
    });
  }

  return texts;
}

// --- Helper: Get Question Answer Tips (from question-answer-detail.xml logic) ---
function getQuestionAnswerTips(answers, qaDetailData, personName, gender) {
  const tips = [];
  
  if (!qaDetailData || !Array.isArray(qaDetailData)) return tips;

  // qaDetailData items have: { qid, type, values: [], text }
  // We need to iterate over all answers we have.
  
  // Note: answers is object { '1': -2, '2': 0, ... }
  Object.keys(answers).forEach(qid => {
     const answerVal = parseInt(answers[qid]);
     if (isNaN(answerVal)) return;
     
     const matches = qaDetailData.filter(item => 
       item.qid === qid && item.values.includes(answerVal)
     );

     matches.forEach(match => {
       tips.push(doGenderNameReplacements(match.text, null, personName, gender));
     });
  });

  return tips;
}


export function calculateRelationshipScore(
  rawAnswers, 
  quiz = DEFAULT_QUIZ, 
  meetingIndex = "1", 
  buyerName = "the buyer", 
  buyerGender = "male"
) {
  
  // 1. Determine Scoring Context (Branching)
  const q9Answer = rawAnswers['9'];
  const isYouCalled = q9Answer !== undefined && parseInt(q9Answer) < 0; 
  const branchKey = isYouCalled ? 'a' : 'b';
  
  const activeQuestionIds = [
    ...quiz.questions.map(q => q.id),
    ...quiz.branches[branchKey].map(q => q.id)
  ];

  // 2. Initialize Scores
  const actual = { S: 0, A: 0, T: 0, P: 0 };
  const max = { S: 0, A: 0, T: 0, P: 0 };

  // 3. Process Questions
  activeQuestionIds.forEach(qid => {
    let q = quiz.questions.find(q => q.id === qid);
    if (!q) {
      q = quiz.branches[branchKey].find(q => q.id === qid);
    }
    if (!q) return;

    const ansVal = rawAnswers[qid];
    if (ansVal === undefined || ansVal === null) return;

    const shift = parseInt(ansVal);
    if (isNaN(shift)) return;
    
    // Side Scores
    let leftScore = 0, rightScore = 0;
    
    if (q.isBinary) {
        if (shift < 0) { // Left Selected
            leftScore = 5; rightScore = 0;
        } else { // Right Selected
            leftScore = 0; rightScore = 5;
        }
    } else {
        // Standard Range Logic: 1-5 Scale
        // -2 -> L=5, R=1; 0 -> L=3, R=3; +2 -> L=1, R=5
        leftScore = 3 - shift;
        rightScore = 3 + shift;
    }

    // Apply Weightings
    const typesInQuestion = new Set();

    if (q.weightings.left && q.weightings.left.weightings) {
        q.weightings.left.weightings.forEach(type => {
            actual[type] = (actual[type] || 0) + leftScore;
            typesInQuestion.add(type);
        });
    }
    if (q.weightings.right && q.weightings.right.weightings) {
        q.weightings.right.weightings.forEach(type => {
            actual[type] = (actual[type] || 0) + rightScore;
            typesInQuestion.add(type);
        });
    }

    // Update Max Scores
    typesInQuestion.forEach(type => {
        max[type] = (max[type] || 0) + 5;
    });
  });

  // 4. Calculate Percentages
  const percentages = {
    S: max.S ? (actual.S / max.S) * 100 : 0,
    A: max.A ? (actual.A / max.A) * 100 : 0,
    T: max.T ? (actual.T / max.T) * 100 : 0,
    P: max.P ? (actual.P / max.P) * 100 : 0
  };

  // 5. Final Classification
  const pPercent = percentages.P;
  let bestType = '';
  
  if (pPercent >= 70) {
      // Only scores >= 70 can be classified as Partnership
      bestType = 'P';
  } else {
      // For scores below 70, classify only as S, A, or T
      const nonPartnershipTypes = ['S', 'A', 'T'];
      let maxVal = -1;
      
      nonPartnershipTypes.forEach(type => {
          if (percentages[type] > maxVal) {
              maxVal = percentages[type];
              bestType = type;
          }
      });
  }

  const pScore = Math.round(pPercent);
  const bestTypeLabel = quiz.typeDefinitions.find(t => t.id === bestType)?.label || bestType;
  const scoreString = `${pScore}${bestType}`;

  // 6. Generate Legacy Content
  
  // A. Classification Text (detail1 equivalent)
  // "Your interaction with [name] in this meeting was indicative of an <TYPE> relationship that has a Partnership component of [pScore] (70 or more indicates the overall relationship is one of Partnership)."
  const typeLabel = bestTypeLabel;
  const article = ['A', 'E', 'I', 'O', 'U'].includes(typeLabel[0].toUpperCase()) ? 'an' : 'a';
  
  let classificationText = `Your interaction with [Name] in this meeting was indicative of ${article} ${typeLabel} relationship that has a Partnership component of ${pScore} (70 or more indicates the overall relationship is one of Partnership).`;
  classificationText = doGenderNameReplacements(classificationText, null, buyerName, buyerGender);

  // B. Score Detail (scoreDetail.xml)
  // Uses helper: getScoreDetailTexts
  const scoreDetailData = quiz.classificationRules?.scoreDetailBands || {};
  const scoreDetail = getScoreDetailTexts(bestType, pScore, meetingIndex, buyerName, buyerGender, scoreDetailData);

  // C. Question Answer Detail (question-answer-detail.xml)
  let questionAnswerDetail = getQuestionAnswerTips(rawAnswers, quiz.qaDetail, buyerName, buyerGender);
  
  // Filter QA tips by bestType
  if (quiz.qaDetail && quiz.qaDetail.length > 0 && quiz.qaDetail[0].type) {
     const filteredData = quiz.qaDetail.filter(d => d.type === bestType);
     questionAnswerDetail = getQuestionAnswerTips(rawAnswers, filteredData, buyerName, buyerGender);
  }

  // 7. Assemble Result Object
  return {
    score: {
      bestType,
      pScore
    },
    classificationText,      // detail1
    scoreDetail,             // detail2 is scoreDetail[0], others are tips
    questionAnswerDetail,
    
    // Legacy/UI Compat props
    standardAssessment: classificationText,
    scoreString,
    bestTypeLabel,
    meetingIndex
  };
}